import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AumicoCoreProvider } from '@modeso/aumico-lib-core-fe';
import {
    DocumentDto,
    ExportTasksFilterState,
    ExportTasksSectionExcelData,
    TaskState,
} from '@modeso/aumico-lib-cpmm-fe';
import { FilePreviewStatus } from '@modeso/aumico-lib-cpmm-fe/enums/FilePreviewStatus.enum';
import { WorkflowArea } from '@modeso/aumico-lib-cpmm-fe/enums/workflow-area.enum';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { UpdateDocumentDto } from '../../../../aumico-lib-cpmm-fe/src/lib/dtos/update-document-dto.interface';
import { DocumentModel } from '../../../../aumico-lib-cpmm-fe/src/lib/interfaces/document.interface';
import { AumicoAnnualReportConfigService } from '../aumico-annualreport.config';
import { ITasksExportTableHeaders } from '../dtos/tasks-export-table-headers.dto';
// Import InventoryData type from its correct location
import { InventoryData } from '../dtos/inventory-data.dto'; // Adjust the path as needed
import { GetOrganizationDto } from 'projects/aumico-lib-users-fe/src/public-api';

@Injectable({
    providedIn: 'root',
})
export class WorkflowDocumentsService {
    private server = '';
    private readonly tasksRoute = 'tasks';
    private readonly projectsRoute = 'projects';
    private readonly documentsRoute = 'documents';

    constructor(
        private http: HttpClient,
        @Inject(AumicoCoreProvider) private coreProvider: AumicoCoreProvider,

        @Inject(AumicoAnnualReportConfigService) private config,
    ) {
        this.server = config.apiGateway;
    }

    loadDocuments$(taskId: string): Observable<DocumentDto[]> {
        const url = `${this.server}${this.tasksRoute}/${taskId}/documents`;
        this.coreProvider.dispatchBlockingRequest('loadDocuments$');
        return this.http
            .get<DocumentDto[]>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('loadDocuments$')));
    }

    uploadDocument$(taskId: string, files: File[], document: Partial<DocumentModel>): Observable<DocumentDto[]> {
        const url = `${this.server}${this.tasksRoute}/${taskId}/documents`;

        const formData = new FormData();
        files.forEach((file) => formData.append('files', file, file.name));

        // Add document metadata to FormData
        if (document.name) {
            formData.append('documentName', document.name);
        }
        if (document.isPermanent !== undefined) {
            formData.append('isPermanent', document.isPermanent.toString());
        }
        if (document.expiryDate) {
            formData.append('expiryDate', document.expiryDate.toISOString());
        }
        if (document.externalLinkUrl) {
            formData.append('externalUrl', document.externalLinkUrl);
        }

        // this.coreProvider.dispatchBlockingRequest('uploadDocument$');
        return this.http
            .post<DocumentDto[]>(url, formData)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('uploadDocument$')));
    }

    updateDocumentMetaData$(document: Partial<DocumentModel>): Observable<DocumentDto> {
        const taskId = document.taskId;
        if (!taskId) {
            throw new Error('Task ID is required to update document metadata');
        }
        const url = `${this.server}${this.tasksRoute}/${taskId}/documents`;

        // Add document metadata to FormData
        const updateDocumentDto: UpdateDocumentDto = {
            id: document.id,
            documentName: document.name,
            isPermanent: document.isPermanent,
            expiryDate: document.expiryDate,
            externalUrl: document.externalLinkUrl,
            taskId: document.taskId,
        };

        // this.coreProvider.dispatchBlockingRequest('uploadDocument$');
        return this.http
            .put<DocumentDto>(url, updateDocumentDto)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('uploadDocument$')));
    }

    deleteDocument$(documentId: string, projectId: string): Observable<void> {
        const url = `${this.server}${this.documentsRoute}/${documentId}`;
        this.coreProvider.dispatchBlockingRequest('deleteDocument$');
        return this.http
            .delete<void>(url, { body: { projectId } })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('deleteDocument$')));
    }

    exportProjectDocuments$(
        projectId: string,
        companyName: string,
        balanceSheetDate: string,
        workflowAreas: WorkflowArea[],
        inventoryData: InventoryData,
    ): Observable<Blob> {
        const url = `${this.server}${this.projectsRoute}/${projectId}/documents/export`;
        this.coreProvider.dispatchBlockingRequest('exportProjectDocuments$');
        
        const requestBody: any = {
            companyName,
            balanceSheetDate,
            workflowAreas,
        };
        
        // Add inventoryData to request body if provided
        if (inventoryData) {
            requestBody.inventoryData = inventoryData;
        }
        
        return this.http
            .post<Blob>(
                url,
                requestBody,
                { responseType: 'blob' as 'json' },
            )
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('exportProjectDocuments$')));
    }

    exportTasksDataAsExcel$(payload: {
        tableHeaders: ITasksExportTableHeaders;
        data: ExportTasksSectionExcelData[];
        fileName: string;
        tasksFilter: (TaskState | ExportTasksFilterState)[];
        tableTitle: string;
        organization: GetOrganizationDto; 
    }): Observable<any> {
        const url = `${this.server}exporting/tasks-xlsx`;
        this.coreProvider.dispatchBlockingRequest('exportTasksDataAsExcel$');
        return this.http
            .post(url, payload, { responseType: 'blob', observe: 'body' })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('exportTasksDataAsExcel$')));
    }

    /**
     * Preview: check current generation status for a file.
     */
    getPreviewStatus(fileId: string): Observable<{ status: FilePreviewStatus }> {
        const url = `${this.server}file-preview/status/${fileId}?_=${new Date().getTime()}`;
        return this.http.get<{ status: FilePreviewStatus }>(url);
    }

    /**
     * Preview: request generation of a preview for a file.
     */
    requestPreviewGeneration(fileId: string): Observable<{ requestId: string }> {
        const url = `${this.server}file-preview/generate/${fileId}`;
        return this.http.post<{ requestId: string }>(url, {});
    }

    /**
     * Preview: fetch generated preview URL for a file.
     */
    getPreview(fileId: string): Observable<{ url: string }> {
        const url = `${this.server}file-preview/${fileId}`;
        return this.http.get<{ url: string }>(url);
    }

    /**
     * Fetches the content of a file as a Blob.
     */
    getFileContent(fileId: string): Observable<Blob> {
        const url = `${this.server}file-metadata/${fileId}/content`; // Assumed BE endpoint
        this.coreProvider.dispatchBlockingRequest('getFileContent');
        return this.http
            .get(url, { responseType: 'blob' })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getFileContent')));
    }
}
